import React, { useEffect, useState } from 'react';
import './user-management.scss';

import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import { Typography, Button, ListTable } from 'design_system/src';
import { User } from 'models/user-management/interfaces/user-management.interface';
import {
	fetchUsers,
	updateUser,
	resetUserPassword,
} from 'models/user-management/controllers/users.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import EditUser from './edit/EditUser';

function Users() {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedUser, setSelectedUser] = useState<User | undefined>();
	const [openEditModal, setOpenEditModal] = useState<boolean>(false);

	// Fetch users
	const loadUsers = async () => {
		setLoading(true);
		try {
			const data = await fetchUsers();
			// Ensure data is an array
			setUsers(Array.isArray(data) ? data : []);
		} catch (error: any) {
			setUsers([]);
			toasterNotification({
				msg: error?.message || 'Error loading users',
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadUsers();
	}, []);

	const handleCreate = () => {
		setSelectedUser(undefined);
		setOpenEditModal(true);
	};

	const handleEdit = (user: User) => {
		setSelectedUser(user);
		setOpenEditModal(true);
	};

	const handleToggleActive = async (user: User) => {
		const newStatus = !user.isActive;
		const action = newStatus ? 'activate' : 'set as inactive';
		if (
			window.confirm(`Are you sure you want to ${action} user "${user.email}"?`)
		) {
			try {
				const userId =
					typeof user._id === 'string' ? user._id : String(user._id);
				await updateUser(userId, { isActive: newStatus });
				toasterNotification({
					msg: `User ${
						newStatus ? 'activated' : 'set as inactive'
					} successfully`,
					toasterType: toasterOptions.success,
				});
				loadUsers();
			} catch (error: any) {
				const errorMessage =
					extractErrorMessage(error) ||
					`Error ${newStatus ? 'activating' : 'setting user as inactive'}`;
				toasterNotification({
					msg: errorMessage,
					toasterType: toasterOptions.error,
				});
			}
		}
	};

	const handleResetPassword = async (user: User) => {
		if (
			window.confirm(
				`Are you sure you want to send a password reset email to "${user.email}"?`,
			)
		) {
			try {
				const userId =
					typeof user._id === 'string' ? user._id : String(user._id);
				await resetUserPassword(userId);
				toasterNotification({
					msg: 'Password reset email sent successfully',
					toasterType: toasterOptions.success,
				});
			} catch (error: any) {
				const errorMessage =
					extractErrorMessage(error) || 'Error sending password reset email';
				toasterNotification({
					msg: errorMessage,
					toasterType: toasterOptions.error,
				});
			}
		}
	};

	const handleCloseModal = () => {
		setOpenEditModal(false);
		setSelectedUser(undefined);
	};

	const handleSaveSuccess = () => {
		loadUsers();
		handleCloseModal();
	};

	const cols = [
		{
			label: 'Email',
			attribute: 'email' as keyof User,
		},
		{
			label: 'Name',
			render: (
				user: User,
				index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => {
				const name = user.name || '';
				const lastName = user.lastName || '';
				return (
					<BaseTypography>
						{`${name || ''} ${lastName || ''}`.trim() || '-'}
					</BaseTypography>
				);
			},
		},
		{
			label: 'Phone',
			attribute: 'phone' as keyof User,
		},
		{
			label: 'Company',
			render: (
				user: User,
				index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => {
				const companyName =
					typeof user.company === 'object' && user.company
						? user.company.name
						: '-';
				return <BaseTypography>{companyName}</BaseTypography>;
			},
		},
		{
			label: 'Active',
			render: (
				user: User,
				index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => <BaseTypography>{user.isActive ? 'Yes' : 'No'}</BaseTypography>,
		},
		{
			label: 'Actions',
			render: (
				user: User,
				index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => (
				<div style={{ display: 'flex', gap: '8px' }}>
					<Button
						variant="outline"
						scale="mini"
						onClick={() => handleEdit(user)}
					>
						<Typography scale="xsmall" weight="400">
							Edit
						</Typography>
					</Button>
					<Button
						variant="outline"
						scale="mini"
						color={user.isActive ? 'red' : 'green'}
						onClick={() => handleToggleActive(user)}
					>
						<Typography scale="xsmall" weight="400">
							{user.isActive ? 'Inactivate' : 'Activate'}
						</Typography>
					</Button>
					<Button
						variant="outline"
						scale="mini"
						color="neutral"
						onClick={() => handleResetPassword(user)}
					>
						<Typography scale="xsmall" weight="400">
							Reset Password
						</Typography>
					</Button>
				</div>
			),
		},
	];

	return (
		<div className="user-management-container">
			<div className="user-management-header">
				<Typography scale="heading3" weight="600">
					Users Management
				</Typography>
				<Button variant="principal" icon={<AddCircle />} onClick={handleCreate}>
					Create User
				</Button>
			</div>

			{loading ? (
				<div style={{ padding: '24px', textAlign: 'center' }}>
					<Typography scale="medium" weight="400">
						Loading users...
					</Typography>
				</div>
			) : (
				<ListTable selectId="_id" canSelect={false} data={users} cols={cols} />
			)}

			{openEditModal && (
				<EditUser
					user={selectedUser}
					onClose={handleCloseModal}
					onSave={handleSaveSuccess}
				/>
			)}
		</div>
	);
}

export default Users;
